import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const FinalSubmissionPanel = ({ 
  isReadyToSubmit, 
  onSubmit, 
  registrationData,
  registrationId 
}) => {
  const navigate = useNavigate();
  const [confirmations, setConfirmations] = useState({
    accuracy: false,
    documents: false,
    eligibility: false,
    consequences: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const allConfirmed = Object.values(confirmations)?.every(value => value === true);
  const canSubmit = isReadyToSubmit && allConfirmed;

  const handleConfirmationChange = (key, checked) => {
    setConfirmations(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleSubmit = async () => {
    if (!canSubmit || !registrationId) return;

    setIsSubmitting(true);

    try {
      // Call the onSubmit handler which will submit to Supabase
      await onSubmit(registrationId);
      
      // Show success modal
      setIsSubmitting(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting registration:', error);
      setIsSubmitting(false);
      alert(`Failed to submit registration: ${error.message || 'Unknown error'}`);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/registration-dashboard');
  };

  return (
    <>
      <div className="card sticky top-4">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Send" size={20} color="var(--color-primary)" />
          Final Submission
        </h3>

        {/* Registration Summary */}
        <div className="mb-6 p-4 bg-muted/30 rounded-lg">
          <h4 className="font-medium text-foreground mb-3">Registration Summary</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">School:</span>
              <span className="font-medium text-foreground">{registrationData?.schoolName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Category:</span>
              <span className="font-medium text-foreground">{registrationData?.ageCategory}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Players:</span>
              <span className="font-medium text-foreground">{registrationData?.playerCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Documents:</span>
              <span className="font-medium text-success flex items-center gap-1">
                <Icon name="CheckCircle" size={14} />
                All Uploaded
              </span>
            </div>
          </div>
        </div>

        {/* Confirmation Checkboxes */}
        <div className="mb-6 space-y-3">
          <h4 className="font-medium text-foreground mb-3">Final Confirmations</h4>
          
          <Checkbox
            label="I confirm that all information provided is accurate and complete"
            checked={confirmations?.accuracy}
            onChange={(e) => handleConfirmationChange('accuracy', e?.target?.checked)}
            size="sm"
          />

          <Checkbox
            label="All uploaded documents are genuine and unaltered"
            checked={confirmations?.documents}
            onChange={(e) => handleConfirmationChange('documents', e?.target?.checked)}
            size="sm"
          />

          <Checkbox
            label="All players meet the age eligibility criteria for the selected category"
            checked={confirmations?.eligibility}
            onChange={(e) => handleConfirmationChange('eligibility', e?.target?.checked)}
            size="sm"
          />

          <Checkbox
            label="I understand that false information may lead to team disqualification"
            checked={confirmations?.consequences}
            onChange={(e) => handleConfirmationChange('consequences', e?.target?.checked)}
            size="sm"
          />
        </div>

        {/* Warning Message */}
        {!isReadyToSubmit && (
          <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-foreground">
                Please complete all required steps before submitting your registration.
              </p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          variant="default"
          size="lg"
          fullWidth
          disabled={!canSubmit}
          loading={isSubmitting}
          iconName="Send"
          iconPosition="left"
          onClick={handleSubmit}
        >
          {isSubmitting ? 'Submitting Registration...' : 'Submit Registration'}
        </Button>

        {/* Info Note */}
        <div className="mt-4 p-3 bg-muted/30 rounded-lg space-y-2">
          <p className="text-xs text-muted-foreground flex items-start gap-2">
            <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              After submission, your registration will be reviewed by our team. You will receive confirmation via WhatsApp and SMS within 2-3 business days.
            </span>
          </p>
          <p className="text-xs text-muted-foreground flex items-start gap-2">
            <Icon name="MessageSquare" size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              <strong>SMS Updates:</strong> You will receive registration status updates (approval/rejection notifications) via SMS from <strong>LOOPEN</strong> short code. Please ensure your mobile number can receive messages from this sender.
            </span>
          </p>
        </div>
      </div>
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-lg max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={40} color="var(--color-success)" />
            </div>

            <h3 className="text-2xl font-bold text-foreground mb-2">
              Registration Submitted Successfully!
            </h3>

            <p className="text-muted-foreground mb-6">
              Your team registration has been submitted for review. Please note your registration ID for future reference.
            </p>

            <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Registration ID</p>
              <p className="text-2xl font-bold text-primary font-mono">
                {registrationData?.registrationNumber || registrationId || 'N/A'}
              </p>
            </div>

            <div className="mb-6 space-y-3 text-left">
              <div className="flex items-start gap-3">
                <Icon name="MessageSquare" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">WhatsApp Confirmation</p>
                  <p className="text-sm text-muted-foreground">
                    You will receive a confirmation message on your registered mobile number
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="Clock" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Review Timeline</p>
                  <p className="text-sm text-muted-foreground">
                    Your registration will be reviewed within 2-3 business days
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="Bell" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Status Updates</p>
                  <p className="text-sm text-muted-foreground">
                    Track your registration status in the dashboard
                  </p>
                </div>
              </div>
            </div>

            <Button
              variant="default"
              size="lg"
              fullWidth
              onClick={handleSuccessClose}
              iconName="ArrowRight"
              iconPosition="right"
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default FinalSubmissionPanel;